import {
  Button,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Typography,
} from "@mui/material"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ReactComponent as DownArrow } from "../../assets/icons/order_icon/date_picker/white_down_arrow.svg"
import { ReactComponent as DesignNotFound } from "../../assets/images/NoRecommended.svg"
import GalleryItem from "../../components/ui/GalleryItem"
import { useGlobalContext } from "../../context/GlobalContext"
import LongCardProduct from "./LongCardProduct"

const MemoizedProductCard = React.memo(LongCardProduct)

const CustomCollapse = ({ isOpen, children }) => {
  const contentRef = useRef(null)
  const [maxHeight, setMaxHeight] = useState(isOpen ? "none" : "0")
  const [overflow, setOverflow] = useState(isOpen ? "visible" : "hidden")

  useEffect(() => {
    if (!contentRef.current) return

    if (isOpen) {
      const contentHeight = contentRef.current.scrollHeight
      setMaxHeight(`${contentHeight}px`)
      setOverflow("hidden")

      const timer = setTimeout(() => {
        setMaxHeight("none")
        setOverflow("visible")
      }, 300)

      return () => clearTimeout(timer)
    } else {
      const contentHeight = contentRef.current.scrollHeight
      setMaxHeight(`${contentHeight}px`)
      setOverflow("hidden")

      // Force reflow
      // eslint-disable-next-line no-unused-expressions
      contentRef.current.offsetHeight

      setTimeout(() => {
        setMaxHeight("0")
      }, 20)
    }
  }, [isOpen])

  return (
    <div
      ref={contentRef}
      style={{
        maxHeight,
        overflow,
        transition: "max-height 300ms ease-out",
      }}>
      {children}
    </div>
  )
}

const CartScreen = () => {
  const {
    search,
    infoFilter,
    setCartScreen,
    setScanned,
    cartData,
    handleRemoveFromCart,
    setDetails,
    setOrderDetails,
    collapse,
    setCollapse,
    setProceedClick,
    hasInvalidSize,
    setFilterModify,
    galleryView,
  } = useGlobalContext()

  const [display, setDisplay] = useState(false)
  const [collapsed, setCollapsed] = useState({})
  const [hoveredId, setHoveredId] = useState("")

  const groupByCategory = useCallback(
    (data) => {
      const grouped = {}
      data.forEach((item) => {
        const category = item?.categoryName || "Others"
        if (!grouped[category]) {
          grouped[category] = []
        }
        grouped[category]?.push(item)
      })

      return Object.entries(grouped)?.map(([category, designs]) => ({
        category,
        count: designs?.length?.toString(),
        designCount: designs?.length?.toString(),
        sizeNotSelected: hasInvalidSize?.filter(
          (item) => item?.categoryName === category
        ).length,
        designs,
      }))
    },
    [hasInvalidSize]
  )

  const categoryList = useMemo(
    () => groupByCategory(cartData),
    [cartData, groupByCategory]
  )

  const filteredCart = useMemo(() => {
    if (search) {
      return categoryList
        .map((category) => {
          const matchedDesigns = category?.designs?.filter((item) =>
            item?.styleNumber?.toLowerCase()?.includes(search.toLowerCase())
          )
          return {
            ...category,
            designs: matchedDesigns,
            count: matchedDesigns?.length.toString(),
            designCount: matchedDesigns?.length > 0 ? "1" : "0",
            sizeNotSelected: hasInvalidSize?.filter(
              (item) => item?.categoryName === category
            ).length,
          }
        })
        .filter((category) => category?.designs?.length > 0)
    } else if (infoFilter) {
      const activeFilter = infoFilter?.trim() || ""

      return (categoryList || [])
        .map((category) => {
          const matchedDesigns = (category?.designs || [])
            .filter((design) => {
              return (design?.quantityData || []).some((qtyItem) => {
                if (activeFilter.startsWith("RH-")) {
                  return qtyItem?.rhodium === activeFilter.slice(3)
                } else if (activeFilter.includes("K-")) {
                  const goldCombination = `${qtyItem?.goldQty}K-${qtyItem?.goldColor}`
                  return goldCombination === activeFilter
                } else {
                  return qtyItem?.diaQty === activeFilter
                }
              })
            })
            .map((design) => ({
              ...design,
              quantityData: (design?.quantityData || []).filter((qtyItem) => {
                if (activeFilter.startsWith("RH-")) {
                  return qtyItem?.rhodium === activeFilter.slice(3)
                } else if (activeFilter.includes("K-")) {
                  const goldCombination = `${qtyItem?.goldQty}K-${qtyItem?.goldColor}`
                  return goldCombination === activeFilter
                } else {
                  return qtyItem?.diaQty === activeFilter
                }
              }),
            }))

          return {
            ...category,
            designs: matchedDesigns,
            count: matchedDesigns.length.toString(),
            designCount: matchedDesigns.length > 0 ? "1" : "0",
            sizeNotSelected: (hasInvalidSize || []).filter(
              (item) => item?.categoryName === category?.categoryName
            ).length,
          }
        })
        .filter((category) => (category?.designs || []).length > 0)
    }

    return categoryList
  }, [search, categoryList, infoFilter])

  useEffect(() => {
    const allDesignsEmpty = categoryList.every(
      (category) => category.designs.length < 1
    )

    if (allDesignsEmpty) {
      localStorage.removeItem("scanned")
      setScanned(false)
      setCartScreen(false)
    }
  }, [categoryList])

  useEffect(() => {
    const timer = setTimeout(() => setDisplay(true), 500)
    return () => {
      clearTimeout(timer)
      setCollapse(false)
    }
  }, [])

  useEffect(() => {
    let diamondWeight = 0
    let goldWeight = 0
    let designCount = 0

    filteredCart.forEach((category) => {
      category.designs.forEach((item) => {
        diamondWeight += item?.diaWeight * item?.quantity || 0
        goldWeight += item?.netWeight * item?.quantity || 0
        designCount += 1
      })
    })

    const uniqueCategories = filteredCart.length

    setDetails({
      prevPurchase: 0,
      diamondWeight,
      goldWeight,
      designCount,
      categories: uniqueCategories,
      cartScore: 0,
    })

    return () => {
      setDetails({
        prevPurchase: 0,
        diamondWeight: 0,
        goldWeight: 0,
        designCount: 0,
        categories: 0,
        cartScore: 0,
      })
      setFilterModify({
        diamondQuality: "",
        goldValue: "",
        size: "",
        categories: [],
      })
    }
  }, [filteredCart])

  useEffect(() => {
    setCollapsed((prev) => {
      const updated = { ...prev }
      let changed = false

      categoryList.forEach(({ category }) => {
        if (updated[category] !== collapse) {
          updated[category] = collapse
          changed = true
        }
      })

      return changed ? updated : prev
    })
  }, [collapse])

  const handleCompleteOrder = useCallback(() => {
    if (hasInvalidSize?.length > 0) {
      setProceedClick(categoryList?.map((item) => item?.category))
      return
    }
    setCartScreen(false)
    setOrderDetails(true)
  }, [hasInvalidSize])

  const toggleCategory = useCallback(
    (category) => {
      setCollapsed((prev) => {
        const newState = {
          ...prev,
          [category]: !prev[category],
        }

        const values = Object.values(newState)
        const allCollapsed = values.every((v) => v === true)
        const allExpanded = values.every((v) => v === false)

        if (allCollapsed) {
          setCollapse(true)
        } else if (allExpanded) {
          setCollapse(false)
        }

        return newState
      })
    },
    [collapse]
  )

  const renderCategories = useMemo(() => {
    return filteredCart.map((categoryData, index) => {
      if (!categoryData?.designs || categoryData?.designs?.length < 1) {
        return null
      }
      return (
        <React.Fragment key={`${categoryData?.category}-${index}`}>
          <Typography
            fontFamily={"Poppins"}
            fontSize={".8vw"}
            fontWeight={600}
            margin={"1vw"}
            padding="0 .2vw"
            onClick={() => toggleCategory(categoryData?.category)}
            sx={{ cursor: "pointer" }}>
            <div
              style={{
                padding: ".3vw",
                display: "flex",
                gap: ".3vw",
                alignItems: "center",
              }}>
              <DownArrow
                style={{
                  transform: collapsed[categoryData?.category]
                    ? "rotate(-90deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  marginRight: "8px",
                }}
              />
              {categoryData?.category} -
              <span style={{ fontWeight: "700" }}>
                {categoryData.designCount} Design
                {categoryData.designCount > 1 ? "s" : null}
              </span>
              {!galleryView && (
                <>
                  <span style={{ color: "#595959" }}>
                    (
                    {categoryData.designs?.reduce(
                      (sum, item) => sum + (item?.quantityData?.length || 0),
                      0
                    )}{" "}
                    Article
                    {categoryData.designs?.reduce(
                      (sum, item) => sum + (item?.quantityData?.length || 0),
                      0
                    ) > 1
                      ? "s"
                      : null}
                    )
                  </span>
                  {categoryData?.sizeNotSelected >= 1 && (
                    <span
                      style={{
                        border: ".05vw solid #F23D3D",
                        color: "#F23D3D",
                        fontSize: ".7vw",
                        fontWeight: "400",
                        padding: ".2vw .4vw",
                        borderRadius: ".45vw",
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setProceedClick((prev) => [
                          ...prev,
                          categoryData?.category,
                        ])
                      }}>
                      {categoryData.designs?.reduce((sum, item) => {
                        const invalidCount =
                          item?.quantityData?.filter(
                            (q) =>
                              q.itemSize === "" &&
                              item?.subCategoryName != "Without Chain"
                          )?.length || 0
                        return sum + invalidCount
                      }, 0)}{" "}
                      Article
                      {categoryData.designs?.reduce((sum, item) => {
                        const invalidCount =
                          item?.quantityData?.filter(
                            (q) =>
                              q.itemSize === "" &&
                              item?.subCategoryName != "Without Chain"
                          )?.length || 0
                        return sum + invalidCount
                      }, 0) > 1
                        ? "s"
                        : null}{" "}
                      left
                    </span>
                  )}
                </>
              )}
            </div>
          </Typography>
          <div style={{ padding: "0 1vw" }}>
            <CustomCollapse isOpen={!collapsed[categoryData?.category]}>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1.8vw",
                  rowGap: "1.8vw",
                }}>
                {galleryView
                  ? categoryData?.designs?.map((design) => (
                      <GalleryItem
                        key={design?.styleNumber}
                        design={design}
                        hoveredId={hoveredId}
                        setHoveredId={setHoveredId}
                        onDelete={() => handleRemoveFromCart(design)}
                      />
                    ))
                  : categoryData?.designs?.flatMap((design) =>
                      design?.quantityData?.map((item) => (
                        <MemoizedProductCard
                          key={item?.key}
                          data={design}
                          modifyData={item}
                          setIcon
                          onDelete={() =>
                            handleRemoveFromCart(design, item?.key)
                          }
                        />
                      ))
                    )}
              </Grid>
            </CustomCollapse>
          </div>
          <Divider
            sx={{
              border: "0.15vw solid",
              borderColor: "#0D0D0D",
              width: "100%",
              mt: collapsed[categoryData?.category] ? "0vw" : "1.5vw",
            }}
          />
        </React.Fragment>
      )
    })
  }, [filteredCart, collapsed, galleryView, hoveredId])

  return (
    <>
      <div
        style={{
          width: "100%",
          height: "100%",
          overflowY: "scroll",
          overflowX: "hidden",
          borderTop: "0.05vw solid #0d0d0d",
        }}>
        <Fade in={display} timeout={800}>
          <div>
            {display ? (
              filteredCart.length > 0 ? (
                renderCategories
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "60vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}>
                    <DesignNotFound />
                    <Typography
                      sx={{
                        color: "#B3B3B3",
                        fontFamily: "Poppins",
                        fontSize: ".7vw",
                        fontWeight: 400,
                      }}>
                      Design Not Found
                    </Typography>
                  </div>
                </div>
              )
            ) : null}
          </div>
        </Fade>
        {!display && (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <CircularProgress color="success" />
          </div>
        )}
      </div>

      {display && filteredCart.length > 0 && (
        <Button
          sx={{
            width: "100%",
            height: "2.4vw",
            background: hasInvalidSize?.length > 0 ? "#8B8B8B" : "#50C878",
            color: "#000",
            fontFamily: "Poppins",
            fontSize: ".8vw",
            fontWeight: "600",
            borderRadius: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 .8vw",
          }}
          onClick={handleCompleteOrder}>
          PROCEED
        </Button>
      )}
    </>
  )
}

export default React.memo(CartScreen)
